import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button.js";

interface InviteOnboardingProps {
  onComplete: () => void;
  userName?: string;
  workspaceName?: string;
}

export function InviteOnboarding({
  onComplete,
  userName,
  workspaceName,
}: InviteOnboardingProps) {
  const firedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const completeIntro = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setError(null);
    void (async () => {
      try {
        const response = await fetch("/api/workspace/onboarding", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ introCompleted: true }),
        });
        if (!response.ok) {
          throw new Error("Failed to finish intro");
        }
        onComplete();
      } catch {
        firedRef.current = false;
        setError("We could not finish your intro. Try again to open Work.");
      }
    })();
  }, [onComplete]);

  useEffect(() => {
    completeIntro();
  }, [completeIntro]);

  return (
    <div className="h-full grid place-items-center [background:var(--canvas-field)] p-6">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl font-semibold text-[var(--text-primary)]">
          {error ? "Ready when you are" : "Opening Work"}
        </h1>
        <p className="mt-3 font-[var(--font-prose)] text-[16px] leading-[1.55] text-[var(--ink-body)]">
          {error
            ? error
            : `${userName ? `${userName}, y` : "Y"}ou're joining ${workspaceName ?? "this workspace"}.`}
        </p>
        {error && (
          <Button
            type="button"
            className="mt-5"
            onClick={completeIntro}
          >
            Open Work
          </Button>
        )}
      </div>
    </div>
  );
}
